import { Container } from "@mui/material";
import { useState } from "react";

import Page from "~components/Page";
import HeaderBreadcrumbs from "~src/components/HeaderBreadcrumbs";
import { PATH_BEST_SELLING_PRODUCTS } from "~src/routes/paths";
import { TrendingProductsList } from "~src/sections/winningProducts/TrendingProductsList";
import useSettings from "../../hooks/useSettings";
import { useInfiniteBestSellingProducts } from "~src/sections/winningProducts/useInfiniteWinningProducts";
import { Header } from "~src/sections/winningProducts/Header";
import { BrandResult } from "~src/sections/competitorAnalytics/SearchBar";
import useFilterPersistence from "~src/hooks/useFilterPersistence";

// ---------------------------------------------------------------------

export interface BestSellingProductsFilters {
  country_code?: string[];
  ad_status?: string;
  brand_ids?: string;
  product_added?: string;
  product_price?: string;
  media_type?: string;
  platform?: string;
  cta?: string[];
  duration?: string[];
  page_likes?: string;
  new_arrivals?: boolean;
  product_rank?: string;
  product_category?: string;
  search_text?: string;
}

export default function BestSellingProducts() {
  const { themeStretch } = useSettings();

  const [filters, setFilters] = useFilterPersistence<BestSellingProductsFilters | undefined>(
    'best_selling_products',
    undefined
  );
  const [selected, setSelected] = useState<BrandResult[]>([]);
  const [titleUpdateKey, setTitleUpdateKey] = useState(0);
  const [prevCount, setPrevCount] = useState<number | null | undefined>(undefined);

  const bestSellingProductsContext = useInfiniteBestSellingProducts({
    pageSize: 30,
    filters,
    searchText: filters?.search_text,
  });

  // Use a memoized value instead of a function to ensure consistent updates
  const title = (() => {
    if (bestSellingProductsContext.count !== null && bestSellingProductsContext.count !== undefined) {
      return `Explore over ${bestSellingProductsContext.count.toLocaleString()} products`;
    } else {
      return "Searching for products...";
    }
  })();

  // When count changes, force a title update (update during render, not in useEffect)
  if (bestSellingProductsContext.count !== prevCount) {
    setPrevCount(bestSellingProductsContext.count);
    // Only increment key if this is not the initial render
    if (prevCount !== undefined) {
      setTitleUpdateKey(prev => prev + 1);
    }
  }

  const handleSearch = (search_text: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      search_text
    }));
  };

  const handleFiltersUpdated = (newFilters: BestSellingProductsFilters | undefined) => {
    setFilters(newFilters);
  };

  return (
    <Page title="Best Selling Products">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <HeaderBreadcrumbs
          heading={title}
          subtitle="Search specifically for products that meet your criteria"
          homeIcon="best_selling_products"
          links={[
            {
              name: "Best Selling Products",
              href: PATH_BEST_SELLING_PRODUCTS.root,
            },
          ]}
          key={titleUpdateKey}
        />
        <Header
          filters={filters}
          onFiltersUpdated={handleFiltersUpdated}
          selected={selected}
          setSelected={setSelected}
          handleSearch={handleSearch}
        />
        <TrendingProductsList
          filters={filters}
          searchText={filters?.search_text}
          isCollapse={false}
          bestSellingProductsContext={bestSellingProductsContext}
        />
      </Container>
    </Page>
  );
}
